import { Banner, Alert, Button, Heading } from '@mm/components';
import { usePromoterCRUDActions } from '@mm/state/actions/PromoterCRUDActions';
import { WidgetTypes } from '@mm/state/model/WidgetModel';
import { showInstaChampAlertSelector } from '@mm/state/selectors/featureFlagsSelectors';
import { activeChatbotSelector } from '@mm/state/selectors/viewSelectors';
import instagram from 'assets/instagram.svg';
import igIphoneStoryMentions from 'assets/iphone-ig-story-mentions.png';
import igIphoneStory from 'assets/iphone-ig-story.png';
import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSupportedWidgets } from '../../../../common/components/AddWidgetInsert/AddWidgetInsert';
import {
  PathGraphContext,
  useNewGraphInstance,
} from '../../../../common/widgets/common/PathGraph/PathGraph';
import { BotBuilderRoutes } from '../../../../routes/bot-builder-routes';
import { useEditPagePrimeState } from '../../../common/hooks/use-edit-page-prime-state';
import { BetaIgTitle } from '../../beta/modules/ChatStarters/ActivateIGBanner/ActivateIGBanner';
import { colors } from '../../beta/styles/colors';
import { InactiveChannelAlertSmart } from '../../campaigns/panels/DripCampaignDetailsPanel/InactiveChannelAlert';
import { DialogueSelectorWithReload } from '../common/DialogueSelectorWithReload/DialogueSelectorWithReload';
import { EditStartBotHeader } from '../common/StartBotPromoterPanel/EditStartBotHeader/EditStartBotHeader';
import { StartBotsDialogueEditor } from '../common/StartBotsDialogueEditor/StartBotsDialogueEditor';
import { usePromoterActivator } from '../common/use-promoter-activator';
import styles from './InstagramStoryMentionEditor.module.css';
import { getDeviceInfo } from '../../../../common/hooks/use-device-info';
import { DialoguePreview } from '../../../../modules/DailoguePreview/DialoguePreview';

export const IG_SUPPORTED_WIDGET_TYPES: WidgetTypes[] = [
  WidgetTypes.QUICK_QUESTION,
  WidgetTypes.QQ_OPTIONS,
  WidgetTypes.QQ_EMAIL,
  WidgetTypes.QQ_PHONE,
  WidgetTypes.QQ_INFO,
  WidgetTypes.ATTRIBUTE,
  WidgetTypes.TAGS,
  WidgetTypes.DELAY,
  WidgetTypes.EMAIL,
  WidgetTypes.IMAGE,
  WidgetTypes.GIF,
  WidgetTypes.FORM,
  WidgetTypes.TEXT,
  WidgetTypes.SEND_DATA,
  WidgetTypes.NAVIGATE,
  WidgetTypes.SMS_OPT_IN_V2,
  WidgetTypes.TYPING,
  WidgetTypes.GALLERY,
];

export const InstagramStoryMentionEditor = () => {
  const { widgets, page, promoter } = useEditPagePrimeState(
    'instagram_story_mention',
  );
  const chatbot = useSelector(activeChatbotSelector);
  const pathGraph = useNewGraphInstance(promoter?.pageId);
  const [showInstaChampAlert, instagramUsername] = useSelector(
    showInstaChampAlertSelector,
  );

  const { allWidgets: allSupportedWidgets } = useSupportedWidgets();
  const widgetsToExclude = allSupportedWidgets
    .filter(w => !IG_SUPPORTED_WIDGET_TYPES.includes(w.type))
    .map(w => w.type);
  const { activatePromoter } = usePromoterActivator(promoter);
  const promoterCRUDActions = usePromoterCRUDActions();
  const { isMobile } = getDeviceInfo();

  const dialogueSelectorDropdown = useMemo(() => {
    const promoterDialogueUpdate = async (dialogueId: number) => {
      if (promoter && promoter.id) {
        const updatedPromoter = {
          ...promoter,
          pageId: dialogueId,
        };
        const savedPromoter = await promoterCRUDActions.update(
          chatbot?.id,
          updatedPromoter,
        );

        const newDialogueId = savedPromoter?.pageId;

        if (newDialogueId) {
          const newUrl = BotBuilderRoutes.instagramStoryMentionEdit.getLink({
            chatbotId: chatbot?.id,
            dialogueId: newDialogueId,
            promoterId: promoter.id,
          });
          return {
            newDialogueId,
            newUrl,
          };
        }
      }
    };

    return promoter?.pageId ? (
      <DialogueSelectorWithReload
        dialogueId={promoter.pageId}
        entityUpdateFunction={promoterDialogueUpdate}
      />
    ) : null;
  }, [promoter, chatbot?.id]);

  if (!page || !promoter) {
    return null;
  }

  return (
    <div className={styles.component}>
      <InactiveChannelAlertSmart instagram type="Story Mention" />

      <EditStartBotHeader
        promoter={promoter}
        page={page}
        botIsTemplate={!!chatbot.template}
        customActivateAction={activatePromoter}
      />
      {showInstaChampAlert && (
        <Alert type="warning" showCloseIcon={false}>
          <div>
            This Instagram account was connected via InstaChamp. Making changes
            here may block you from making further changes in InstaChamp.
          </div>
          <Button
            onClick={() => {
              window.location.href = `/instachamp/@${instagramUsername}/story_mentions/${promoter.id}`;
            }}
          >
            Edit with InstaChamp
          </Button>
        </Alert>
      )}

      {!isMobile && (
        <Banner
          css={{ container: styles.ig_story_mentions_banner }}
          title={
            <BetaIgTitle title="Automatically message users who mention your account on an Instagram Story" />
          }
          withArrow
          reverse
          transparentStyle
          headerIcon={<img src={instagram} className={styles.ig_icon} />}
          arrowColor={colors.accentBlue}
          withBannerHeader
          headerText="What's a Story Mention?"
        >
          <div className={styles.images_container}>
            <img src={igIphoneStory} className={styles.phone_icon} />
            <img src={igIphoneStoryMentions} className={styles.phone_icon} />
          </div>
        </Banner>
      )}

      <div className={styles.editor_container}>
        <div className={styles.editor}>
          <Heading size={3}>Choose or create a Dialogue</Heading>
          <div className={styles.dialogue_editor_container}>
            <PathGraphContext.Provider value={pathGraph}>
              {dialogueSelectorDropdown}
              <StartBotsDialogueEditor
                chatbotId={chatbot.id}
                widgets={widgets}
                widgetsToExclude={widgetsToExclude}
                dialogueId={promoter.pageId}
                platforms={['instagram']}
                showAttributesStartWarning
                forInstagram
              />
            </PathGraphContext.Provider>
          </div>
        </div>
        {promoter.pageId && (
          <div className={styles.preview}>
            <DialoguePreview
              platforms={['instagram']}
              dialogueId={promoter?.pageId}
              style={{ top: -20, marginTop: 20 }}
              sticky
            />
          </div>
        )}
      </div>
    </div>
  );
};
